import { Text, Stack, Group } from '@mantine/core'
import Pill, { PillColor } from 'app/views/components/Table/PillUpdated'
import { MasterTradeParams } from 'app/models/master-trade-params'
import { useSelector } from 'react-redux'
import { getDocumentsRegeneratedCount } from 'app/state/ducks/signable-document/selectors'
import { useEffect, useMemo, useRef } from 'react'
import WarningText from 'app/views/components/warning-text/warning-text'
import { MASTER_TRADE_CLOSED_LABEL } from '../utils/master-trade-status'
import { getTradeDetailHeader, getTradeOverview } from 'app/state/ducks/trade-detail/selectors'
import { useTradeRefresh } from './use-trade-refresh'

interface props {
    trade?: MasterTradeParams
}

enum TradeState {
    Unallocated,
    Open,
    Closed,
    Canceled
}

const tradeStatePillColor = new Map<TradeState, PillColor>()
tradeStatePillColor.set(TradeState.Open, PillColor.GREEN)
tradeStatePillColor.set(TradeState.Unallocated, PillColor.PINK)
tradeStatePillColor.set(TradeState.Closed, PillColor.SETTLED_RED)
tradeStatePillColor.set(TradeState.Canceled, PillColor.PINK)

export default function TradeHeader({ trade }: props) {
    const tradeState: TradeState =
        trade?.status === 'Open' ? TradeState.Open :
            trade?.status === 'Settled' ? TradeState.Closed :
                trade?.status === 'Canceled' ? TradeState.Canceled : TradeState.Unallocated
    const status = trade?.status === 'Open' ? 'Open' :
        trade?.status === 'Settled' ? MASTER_TRADE_CLOSED_LABEL :
            trade?.status === 'Canceled' ? 'Canceled' : 'Unallocated'
    const tradeName = trade?.systemId ? `Trade ${trade.systemId.split('-')[1]}` : ''
    // Every tab's read carries a canceled trade's reason on the item itself.
    const cancellationReason = trade?.reason || ''

    // Both warnings come computed with the open tab's read (ADR_LOS-030), by
    // the header's rules run on the server (trade-overview.facts.ts):
    // Overview and Activity Stream from their views (the Activity read carries
    // the Overview's header, LOS-7095), the other tabs from the Sub-trade /
    // Documents read. Until the read for this trade has landed, no warning shows.
    const { isOverviewTab, isActivityTab, isDocumentsTab, refreshTrade } = useTradeRefresh()
    const overview = useSelector(getTradeOverview)
    const detailHeader = useSelector(getTradeDetailHeader)
    const facts = isOverviewTab || isActivityTab
        ? (overview && overview.masterTradeId === trade?.id ? overview.header : null)
        : (detailHeader && detailHeader.masterTradeId === trade?.id ? detailHeader : null)

    // A regenerated document rewrites its AA metadata: re-read the tab so the
    // chip clears without a reload (the read carries the count). On
    // Documents the regenerate itself re-reads the tab.
    const documentsRegeneratedCount = useSelector(getDocumentsRegeneratedCount)
    const seenRegeneratedCount = useRef(documentsRegeneratedCount)
    useEffect(() => {
        if (documentsRegeneratedCount === seenRegeneratedCount.current) return
        seenRegeneratedCount.current = documentsRegeneratedCount
        if (!isDocumentsTab) refreshTrade()
    }, [documentsRegeneratedCount, isDocumentsTab, refreshTrade])

    // Documents whose recorded amount the trade has already moved past - a
    // refresh that did not happen. Deliberately not gated on trade status: a
    // settled trade is the worse case, since its AA has already gone to the
    // agent.
    const notRefreshedCount = facts?.notRefreshedCount ?? 0

    const notRefreshedMessage = notRefreshedCount > 0
        ? `${notRefreshedCount} document${notRefreshedCount === 1 ? '' : 's'} not refreshed`
        : null

    const warningMessage = useMemo(() => {
        // Only show warnings for Open trades
        if (trade?.status !== 'Open') {
            return null
        }

        const hasUnappliedPIK = !!facts?.hasUnappliedPIK
        const hasUnappliedPaydown = !!facts?.hasUnappliedPaydown

        if (hasUnappliedPIK && hasUnappliedPaydown) {
            return 'There are unapplied PIK and Paydown transactions'
        } else if (hasUnappliedPIK) {
            return 'There are unapplied PIK transactions'
        } else if (hasUnappliedPaydown) {
            return 'There are unapplied Paydown transactions'
        }
        return null
    }, [facts, trade?.status])

    return (
        <Stack w='100%'>
            <div className="detailsHeader" style={{ alignItems: 'center' }}>
                <Text className='detailsTitle'>{tradeName}</Text>

                <Group spacing="md" noWrap align="center">
                    <Pill
                        pl="30px !important"
                        pr="30px !important"
                        w="fit-content !important"
                        color={tradeStatePillColor.get(tradeState) ?? PillColor.GREY}
                        text={status as string}
                    />
                    {notRefreshedMessage && (
                        <WarningText type="attention" text={notRefreshedMessage} />
                    )}
                    {warningMessage && (
                        <WarningText text={warningMessage} />
                    )}
                </Group>
            </div>
            {cancellationReason &&
                (
                    <Group w='100%' className='cancellation-reason-wrapper'>
                        <Text className='cancellation-reason-label'>Reason of Cancelation: </Text>
                        <Text className='cancellation-reason-value'>{cancellationReason}</Text>
                    </Group>
                )}
        </Stack>
    )
}